import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, HostListener, NgZone, OnDestroy, OnInit, PLATFORM_ID, Renderer2, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Adds a circular zoom window that follows the mouse over an image.
 * Useful for product images or detail inspection while keeping the page layout intact.
 * Supports keyboard navigation for WCAG 2.1 AAA compliance.
 */
@Directive({
  selector: '[gsImageZoom]',
  standalone: true
})
export class ImageZoomDirective implements OnInit, OnDestroy {
  // Services
  /** Host `<img>` element the directive is attached to. */
  private readonly el = inject(ElementRef);
  /** Renderer used for SSR-safe attribute and style writes on the host image. */
  private readonly renderer = inject(Renderer2);
  /** Translation service for the keyboard-focus aria-label. */
  private readonly translateService = inject(TranslateService);
  /** Used to attach the mousemove listener outside Angular's change detection. */
  private readonly zone = inject(NgZone);
  /** Distinguishes the server render from the browser. */
  private readonly platformId = inject(PLATFORM_ID);

  // Inputs & Models
  /** Enables or disables the zoom behavior. */
  readonly enableZoom = input<boolean>(true);
  /** Magnification factor for the zoomed image. */
  readonly zoomFactor = input<number>(2);
  /** Size of the circular zoom window in pixels. */
  readonly zoomWindowSize = input<number>(120);
  /** Circular border radius in percent for the zoom window (50 = circle). */
  readonly zoomWindowBorderRadius = input<number>(50);

  // Properties & Signals
  /** Pixel distance between the cursor and the zoom-window center. */
  private readonly offset = 20;
  /** Per-keypress step in percent of the image for keyboard navigation. */
  private readonly keyboardStep = 5;
  /** The DOM element used as the zoom window; appended to `document.body` while active. */
  private zoomWindow: HTMLDivElement | null = null;
  /** Whether the zoom window is currently visible. */
  private isZoomActive = false;
  /** Last keyboard zoom position in percent of the host image. */
  private keyboardZoomPosition = { x: 50, y: 50 };
  /** Pending requestAnimationFrame handle for coalesced mousemove updates. */
  private rafHandle: number | null = null;
  /** Most recent pointer event still waiting to be processed in the next frame. */
  private pendingEvent: MouseEvent | null = null;
  /** Detach function for the manually registered mousemove listener. */
  private mouseMoveCleanup: (() => void) | null = null;
  /** Last `img.src` written into the zoom window's `background-image`; refreshed when the host src changes. */
  private currentBgSrc = '';

  /**
   * Sets the keyboard-accessible attributes (tabindex, role, aria-label) on the host
   * image and ensures its parent has a positioning context for any consumer that needs it.
   * The zoom window itself is `position: fixed` and does not rely on the parent's positioning.
   */
  ngOnInit(): void {
    if (!this.enableZoom()) return;

    const img = this.el.nativeElement as HTMLImageElement;
    const imgContainer = img.parentElement;

    // No layout on the server, so the computed position is only known in the browser.
    if (imgContainer && isPlatformBrowser(this.platformId)) {
      const currentPosition = getComputedStyle(imgContainer).position;
      if (currentPosition === 'static') {
        this.renderer.setStyle(imgContainer, 'position', 'relative');
      }
    }

    this.renderer.setAttribute(img, 'tabindex', '0');
    this.renderer.setAttribute(img, 'role', 'button');
    const zoomAriaLabel = this.translateService.instant('components.image.zoomAriaLabel');
    this.renderer.setAttribute(img, 'aria-label', zoomAriaLabel);
  }

  /**
   * Detaches the mousemove listener and removes the zoom window so the directive
   * leaves no DOM nodes or live event handlers behind.
   */
  ngOnDestroy(): void {
    this.detachMouseMove();
    this.removeZoomWindow();
  }

  /**
   * Activates the zoom on pointer entry and registers a native mousemove listener
   * outside the Angular zone so cursor tracking doesn't trigger change detection.
   */
  @HostListener('mouseenter') onMouseEnter() {
    if (!this.enableZoom()) return;
    this.isZoomActive = true;
    this.createZoomWindow();
    this.zone.runOutsideAngular(() => {
      const handler = (event: MouseEvent) => this.scheduleZoomUpdate(event);
      this.el.nativeElement.addEventListener('mousemove', handler, { passive: true });
      this.mouseMoveCleanup = () => this.el.nativeElement.removeEventListener('mousemove', handler);
    });
  }

  /**
   * Tears down the active zoom session when the pointer leaves the host image.
   */
  @HostListener('mouseleave') onMouseLeave() {
    if (!this.enableZoom()) return;
    this.isZoomActive = false;
    this.detachMouseMove();
    this.removeZoomWindow();
  }

  /**
   * Centers the keyboard zoom position so a keyboard-driven session always starts in the middle.
   */
  @HostListener('focus') onFocus() {
    if (!this.enableZoom()) return;
    this.keyboardZoomPosition = { x: 50, y: 50 };
  }

  /**
   * Closes the zoom window when keyboard focus leaves the host image.
   */
  @HostListener('blur') onBlur() {
    if (!this.enableZoom()) return;
    this.isZoomActive = false;
    this.detachMouseMove();
    this.removeZoomWindow();
  }

  /**
   * Handles keyboard navigation: Enter/Space toggle the zoom, Escape closes it,
   * arrow keys move the zoom position in `keyboardStep`-percent increments.
   * @param event the keyboard event from the focused host image
   */
  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    if (!this.enableZoom()) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isZoomActive) {
          this.isZoomActive = false;
          this.detachMouseMove();
          this.removeZoomWindow();
        } else {
          this.isZoomActive = true;
          this.createZoomWindow();
          this.updateKeyboardZoomWindow();
        }
        break;
      case 'Escape':
        if (this.isZoomActive) {
          event.preventDefault();
          this.isZoomActive = false;
          this.detachMouseMove();
          this.removeZoomWindow();
        }
        break;
      case 'ArrowUp':
        if (this.isZoomActive) {
          event.preventDefault();
          this.keyboardZoomPosition.y = Math.max(0, this.keyboardZoomPosition.y - this.keyboardStep);
          this.updateKeyboardZoomWindow();
        }
        break;
      case 'ArrowDown':
        if (this.isZoomActive) {
          event.preventDefault();
          this.keyboardZoomPosition.y = Math.min(100, this.keyboardZoomPosition.y + this.keyboardStep);
          this.updateKeyboardZoomWindow();
        }
        break;
      case 'ArrowLeft':
        if (this.isZoomActive) {
          event.preventDefault();
          this.keyboardZoomPosition.x = Math.max(0, this.keyboardZoomPosition.x - this.keyboardStep);
          this.updateKeyboardZoomWindow();
        }
        break;
      case 'ArrowRight':
        if (this.isZoomActive) {
          event.preventDefault();
          this.keyboardZoomPosition.x = Math.min(100, this.keyboardZoomPosition.x + this.keyboardStep);
          this.updateKeyboardZoomWindow();
        }
        break;
    }
  }

  /**
   * Creates a circular zoom window that follows the mouse cursor.
   * The background image URL is set once here so the per-mousemove update only
   * has to change position and background-size, avoiding redundant style writes.
   */
  private createZoomWindow() {
    if (this.zoomWindow) return;

    const img = this.el.nativeElement as HTMLImageElement;
    const zoomWindow = document.createElement('div');
    const style = zoomWindow.style;
    style.position = 'fixed';
    style.width = `${this.zoomWindowSize()}px`;
    style.height = `${this.zoomWindowSize()}px`;
    style.borderRadius = `${this.zoomWindowBorderRadius()}%`;
    style.border = '2px solid #ffffff';
    style.overflow = 'hidden';
    style.backgroundColor = '#fff';
    style.backgroundRepeat = 'no-repeat';
    style.zIndex = '9999';
    style.boxShadow = '0 3px 10px rgba(0,0,0,0.2)';
    style.pointerEvents = 'none';
    style.transform = 'translate(-50%, -50%)';
    style.backgroundImage = `url(${img.src})`;
    style.willChange = 'left, top, background-position, background-size';
    this.currentBgSrc = img.src;

    this.zoomWindow = zoomWindow;
    document.body.appendChild(zoomWindow);
  }

  /**
   * Refreshes the zoom window's `background-image` only when the host image's `src` has
   * changed since the window was created (e.g. an error fallback swapped the URL); skips
   * the style write otherwise so the hot path stays cheap.
   * @param img the host image element
   */
  private syncBackgroundImage(img: HTMLImageElement) {
    if (!this.zoomWindow || img.src === this.currentBgSrc) return;
    this.zoomWindow.style.backgroundImage = `url(${img.src})`;
    this.currentBgSrc = img.src;
  }

  /**
   * Coalesces high-frequency mousemove events to one DOM update per animation frame.
   * @param event the latest pointer event
   */
  private scheduleZoomUpdate(event: MouseEvent) {
    this.pendingEvent = event;
    if (this.rafHandle !== null) return;
    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = null;
      const pending = this.pendingEvent;
      this.pendingEvent = null;
      if (pending) this.updateZoomWindow(pending);
    });
  }

  /**
   * Updates the zoom window to follow the mouse cursor and show the magnified portion
   * of the image under the cursor. Uses direct style writes (not Renderer2) since this
   * runs outside the Angular zone on every frame; the background-image URL is only
   * re-written when the host image's `src` has changed.
   * @param event the pointer event whose position drives the zoom window
   */
  private updateZoomWindow(event: MouseEvent) {
    if (!this.zoomWindow || !this.isZoomActive) return;

    const img = this.el.nativeElement as HTMLImageElement;
    const rect = img.getBoundingClientRect();

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      this.removeZoomWindow();
      return;
    }

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const bgPosX = -x * this.zoomFactor() * scaleX + this.zoomWindowSize() / 2;
    const bgPosY = -y * this.zoomFactor() * scaleY + this.zoomWindowSize() / 2;
    const bgWidth = rect.width * this.zoomFactor() * scaleX;
    const bgHeight = rect.height * this.zoomFactor() * scaleY;

    this.syncBackgroundImage(img);
    const style = this.zoomWindow.style;
    style.left = `${event.clientX + this.offset}px`;
    style.top = `${event.clientY - this.offset}px`;
    style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
  }

  /**
   * Removes the zoom window from the DOM and cancels any pending frame update.
   */
  private removeZoomWindow() {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
    this.pendingEvent = null;
    if (this.zoomWindow) {
      this.zoomWindow.remove();
      this.zoomWindow = null;
    }
    this.currentBgSrc = '';
  }

  /**
   * Removes the manually attached native mousemove listener.
   */
  private detachMouseMove() {
    if (this.mouseMoveCleanup) {
      this.mouseMoveCleanup();
      this.mouseMoveCleanup = null;
    }
  }

  /**
   * Updates the zoom window position based on the keyboard navigation state.
   * Uses percentage-based positioning relative to the host image dimensions.
   */
  private updateKeyboardZoomWindow() {
    if (!this.zoomWindow || !this.isZoomActive) return;

    const img = this.el.nativeElement as HTMLImageElement;
    const rect = img.getBoundingClientRect();

    const x = (this.keyboardZoomPosition.x / 100) * rect.width;
    const y = (this.keyboardZoomPosition.y / 100) * rect.height;
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const bgPosX = -x * this.zoomFactor() * scaleX + this.zoomWindowSize() / 2;
    const bgPosY = -y * this.zoomFactor() * scaleY + this.zoomWindowSize() / 2;
    const bgWidth = rect.width * this.zoomFactor() * scaleX;
    const bgHeight = rect.height * this.zoomFactor() * scaleY;

    this.syncBackgroundImage(img);
    const style = this.zoomWindow.style;
    style.left = `${rect.left + x + this.offset}px`;
    style.top = `${rect.top + y - this.offset}px`;
    style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
  }
}
